// Shared geometries (built once per scene, reused by every floor through instancing).
// Conventions: a workstation is modelled in "desk space" (origin on the floor under the desk centre,
// the seated person faces +Z and sits at z = -seatOffset). Limbs hang from their pivot along -Y.
import {
  BoxGeometry, BufferAttribute, CapsuleGeometry, Color, ConeGeometry, Curve, CylinderGeometry, ExtrudeGeometry,
  IcosahedronGeometry, PlaneGeometry, Shape, SphereGeometry, TorusGeometry, TubeGeometry, LatheGeometry, Vector2,
  Vector3, RingGeometry,
} from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { DIMS } from './layout.js';
import { CHICKEN_COLORS, CAT_COLORS } from './palette.js';

/** Body part lengths shared by the geometry and the rig. */
export const BODY = Object.freeze({
  hipSeated: 0.44, hipStanding: 0.67,
  torsoY: 0.3, shoulderY: 0.5, shoulderX: 0.215, neckY: 0.6, headY: 0.19, headR: 0.2,
  upperArm: 0.2, foreArm: 0.2, thigh: 0.26, shin: 0.3, hipX: 0.09,
});

/**
 * « La poule de l’étage » (chicken.js, chicken-view.js): pivots of its moving parts, in model units
 * (origin on the ground between the feet, facing +Z; the view scales the whole model by `scale`).
 * Each part's geometry is modelled around its own pivot.
 */
export const CHICKEN_MODEL = Object.freeze({
  scale: 1.12,
  hipX: 0.055, hipY: 0.17,          // legs hang from the hips down to the ground
  torsoY: 0.2,                      // the body pitches around this point (pecking)
  neckY: 0.38, neckZ: 0.12,         // the head turns around the base of the neck
  wingX: 0.14, wingY: 0.37, wingZ: 0.03,
  beakTip: Object.freeze([0, 0.13, 0.162]), // head space
  height: 0.64,                     // top of the comb
});

/**
 * « Le chat de l’étage » (cat.js, cat-view.js): pivots of its moving parts, in model units (origin
 * on the ground under the middle of the body, facing +Z; the view scales the whole model by
 * `scale`). The body pivots around its hind hips (it sits, lies down and stretches around them):
 * the legs, the head and the tail hang from the body, in its space ("torso space").
 */
export const CAT_MODEL = Object.freeze({
  scale: 1.4,
  hipY: 0.2, hipZ: -0.12,           // the hind hips, standing: the torso's pivot
  hindX: 0.064,                     // hind legs (torso space: at the pivot, either side)
  frontX: 0.054, frontZ: 0.25,      // front legs (torso space)
  leg: 0.2,                         // from a hip or a shoulder down to the sole of the paw
  neckY: 0.095, neckZ: 0.3,         // the head turns and nods around the base of the neck (torso space)
  tailY: 0.062, tailZ: -0.062,      // the root of the tail (torso space)
  tail1: 0.14,                      // its straight first part, along its own -Z
  tailArc: Object.freeze({ r: 0.08, angle: 2 }), // then an arc bending toward its own -X (rolled to curl up)
  height: 0.49,                     // tips of the ears, standing
});

function prep(g) {
  const n = g.index ? g.toNonIndexed() : g;
  for (const name of Object.keys(n.attributes)) {
    if (!['position', 'normal', 'uv'].includes(name)) n.deleteAttribute(name);
  }
  if (!n.attributes.uv) {
    const count = n.attributes.position.count;
    n.setAttribute('uv', new BufferAttribute(new Float32Array(count * 2), 2));
  }
  n.morphAttributes = {};
  return n;
}

/** Merges geometries (indexed or not) into one non-indexed geometry with position/normal/uv. */
export function merge(list) {
  const out = mergeGeometries(list.map(prep), false);
  for (const g of list) g.dispose();
  return out;
}

/**
 * Merges [geometry, paint] pairs into one geometry with a colour per vertex (the floor's pets: one
 * draw call per moving part whatever its colours). `paint` is a "#hex" for the whole part, or a
 * function (x, y, z) → "#hex" of the vertex position (the cat's white belly, socks and blaze).
 */
function mergePainted(list) {
  const color = new Color();
  const parts = list.map(([geo, paint]) => {
    const n = prep(geo);
    const pos = n.attributes.position;
    const byVertex = typeof paint === 'function';
    if (!byVertex) color.set(paint);
    const count = pos.count;
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      if (byVertex) color.set(paint(pos.getX(i), pos.getY(i), pos.getZ(i)));
      arr[i * 3] = color.r;
      arr[i * 3 + 1] = color.g;
      arr[i * 3 + 2] = color.b;
    }
    n.setAttribute('color', new BufferAttribute(arr, 3));
    return n;
  });
  const out = mergeGeometries(parts, false);
  for (const [geo] of list) geo.dispose();
  for (const n of parts) n.dispose();
  return out;
}

function ellipsoid(rx, ry, rz, x, y, z, w = 12, h = 8) {
  return new SphereGeometry(1, w, h).scale(rx, ry, rz).translate(x, y, z);
}

/** The chicken's parts, each around its pivot (CHICKEN_MODEL). */
function chickenGeometries(g) {
  const M = CHICKEN_MODEL;
  const C = CHICKEN_COLORS;
  // Leg: a thin shank and three toes plus a back toe, from the hip down to the ground (y = -hipY).
  const ground = -M.hipY;
  const toe = (len, ry) => at(new BoxGeometry(0.014, 0.012, len).translate(0, 0, len / 2), 0, ground + 0.006, -0.008, 0, ry);
  g.chickenLeg = mergePainted([
    [new CylinderGeometry(0.015, 0.017, 0.155, 6).translate(0, -0.08, 0), C.legs],
    [toe(0.075, 0), C.legs], [toe(0.065, 0.5), C.legs], [toe(0.065, -0.5), C.legs], [toe(0.04, Math.PI), C.legs],
  ]);
  // Body (around the torso pivot): a round body, a fuller breast and a fan of tail feathers.
  const feather = (x, rz) => at(new SphereGeometry(1, 8, 6).scale(0.03, 0.1, 0.065), x, 0.2, -0.19, -0.5, 0, rz);
  g.chickenBody = mergePainted([
    [ellipsoid(0.15, 0.135, 0.2, 0, 0.11, -0.02, 14, 10), C.body],
    [new SphereGeometry(0.105, 12, 8).translate(0, 0.12, 0.085), C.body],
    [feather(0, 0), C.tail], [feather(0.045, -0.28), C.tail], [feather(-0.045, 0.28), C.tail],
  ]);
  // Head (around the base of the neck): neck, head, comb, beak, wattle, eyes.
  g.chickenHead = mergePainted([
    [ellipsoid(0.06, 0.095, 0.065, 0, 0.05, 0, 10, 8), C.body],
    [new SphereGeometry(0.072, 12, 10).translate(0, 0.14, 0.04), C.body],
    [ellipsoid(0.012, 0.03, 0.026, 0, 0.215, 0.015, 6, 5), C.comb],
    [ellipsoid(0.013, 0.034, 0.026, 0, 0.228, 0.045, 6, 5), C.comb],
    [ellipsoid(0.012, 0.026, 0.022, 0, 0.212, 0.075, 6, 5), C.comb],
    [at(new ConeGeometry(0.021, 0.055, 8), 0, 0.135, 0.135, Math.PI / 2 + 0.2), C.beak],
    [ellipsoid(0.012, 0.026, 0.014, 0, 0.095, 0.11, 6, 5), C.comb],
    [new SphereGeometry(0.0115, 6, 5).translate(0.056, 0.152, 0.078), C.eye],
    [new SphereGeometry(0.0115, 6, 5).translate(-0.056, 0.152, 0.078), C.eye],
  ]);
  // Wing (around the shoulder, folded along the body; the same for both sides).
  g.chickenWing = mergePainted([[at(ellipsoid(0.028, 0.08, 0.135, 0, 0, 0, 10, 8), 0.006, -0.045, -0.05, 0.12), C.wing]]);
}

/** A circular arc from the origin along -Z, bending toward -X (the tip of the cat's tail). */
class TailArc extends Curve {
  constructor(r, angle) {
    super();
    this.r = r;
    this.angle = angle;
  }

  getPoint(t, target = new Vector3()) {
    const a = t * this.angle;
    return target.set(-this.r * (1 - Math.cos(a)), 0, -this.r * Math.sin(a));
  }
}

/** A tube along `path` whose radius goes from r0 to r1. */
function taperedTube(path, segments, r0, r1, radial = 8) {
  const g = new TubeGeometry(path, segments, r0, radial, false);
  const pos = g.attributes.position;
  const c = new Vector3();
  for (let i = 0; i <= segments; i++) {
    path.getPointAt(i / segments, c);
    const k = 1 + (r1 / r0 - 1) * (i / segments);
    for (let j = 0; j <= radial; j++) {
      const n = i * (radial + 1) + j;
      pos.setXYZ(n, c.x + (pos.getX(n) - c.x) * k, c.y + (pos.getY(n) - c.y) * k, c.z + (pos.getZ(n) - c.z) * k);
    }
  }
  return g;
}

/** The cat's parts (CAT_MODEL), each around its pivot, grey with a white belly, chest, socks and blaze. */
function catGeometries(g) {
  const M = CAT_MODEL;
  const C = CAT_COLORS;
  const fur = (white) => (x, y, z) => (white(x, y, z) ? C.white : C.fur);
  // Body (torso space, the pivot between the hind hips): a long rounded body, white underneath and
  // on the chest.
  g.catBody = mergePainted([
    [ellipsoid(0.098, 0.09, 0.2, 0, 0.032, 0.125, 16, 12), fur((x, y, z) => y < -0.005 || (z > 0.26 && y < 0.05))],
    [ellipsoid(0.086, 0.09, 0.085, 0, 0.032, 0.24, 12, 10), fur((x, y, z) => y < 0.0 || (z > 0.27 && y < 0.06))],
  ]);
  // Head (around the base of the neck): neck, skull with full cheeks, muzzle, nose, eyes and ears;
  // the lower face, the throat and a blaze up the nose are white.
  const face = (x, y, z) => (z > 0.07 && y < 0.07) || (Math.abs(x) < 0.024 && z > 0.095 && y < 0.118) || (z > 0.0 && y < -0.005);
  const ear = (side) => at(new ConeGeometry(0.04, 0.085, 4, 1).scale(1, 1, 0.5), side * 0.056, 0.158, 0.03, -0.12, 0, -side * 0.34);
  const innerEar = (side) => at(new ConeGeometry(0.025, 0.06, 4, 1).scale(1, 1, 0.4), side * 0.055, 0.15, 0.04, -0.12, 0, -side * 0.34);
  g.catHead = mergePainted([
    [ellipsoid(0.068, 0.08, 0.075, 0, 0.0, -0.02, 12, 10), fur(face)],
    [ellipsoid(0.098, 0.086, 0.088, 0, 0.088, 0.052, 16, 12), fur(face)],
    [ellipsoid(0.104, 0.055, 0.07, 0, 0.06, 0.062, 14, 8), fur(face)],
    [ellipsoid(0.044, 0.03, 0.034, 0, 0.054, 0.128, 10, 8), C.white],
    [ellipsoid(0.014, 0.01, 0.009, 0, 0.074, 0.16, 8, 6), C.nose],
    [ellipsoid(0.017, 0.02, 0.008, 0.041, 0.102, 0.132, 8, 6), C.eye],
    [ellipsoid(0.017, 0.02, 0.008, -0.041, 0.102, 0.132, 8, 6), C.eye],
    [ellipsoid(0.006, 0.016, 0.005, 0.041, 0.102, 0.138, 6, 6), C.pupil],
    [ellipsoid(0.006, 0.016, 0.005, -0.041, 0.102, 0.138, 6, 6), C.pupil],
    [ear(1), C.fur], [ear(-1), C.fur], [innerEar(1), C.pink], [innerEar(-1), C.pink],
  ]);
  // Legs (from the shoulder or the hip down to the sole, y = -leg), white socks and paws.
  const sock = fur((x, y) => y < -0.115);
  const paw = (len) => ellipsoid(0.031, 0.022, len, 0, -M.leg + 0.022, 0.012, 10, 6);
  g.catLegFront = mergePainted([
    [ellipsoid(0.04, 0.062, 0.05, 0, -0.025, 0, 10, 8), C.fur],
    [new CylinderGeometry(0.029, 0.025, 0.17, 8).translate(0, -0.095, 0), sock],
    [paw(0.041), C.white],
  ]);
  // Hind leg: a round haunch around the hip, then the shank.
  g.catLegHind = mergePainted([
    [ellipsoid(0.046, 0.072, 0.062, 0, -0.02, 0, 12, 10), fur((x, y) => y < -0.085)],
    [new CylinderGeometry(0.027, 0.024, 0.16, 8).translate(0, -0.105, -0.004), sock],
    [paw(0.034), C.white],
  ]);
  // Tail: a straight root along -Z, then a curved tip from the end of the root (rounded joints and tip).
  const arc = new TailArc(M.tailArc.r, M.tailArc.angle);
  g.catTail1 = mergePainted([
    [new SphereGeometry(0.026, 10, 8), C.fur],
    [at(new CylinderGeometry(0.023, 0.026, M.tail1, 8, 1, true), 0, 0, -M.tail1 / 2, -Math.PI / 2), C.fur],
  ]);
  g.catTail2 = mergePainted([
    [new SphereGeometry(0.023, 10, 8), C.fur],
    [taperedTube(arc, 10, 0.023, 0.014, 8), C.fur],
    [new SphereGeometry(0.014, 8, 6).translate(...arc.getPoint(1).toArray()), C.fur],
  ]);
}

function rbox(w, h, d, r = 0.02, seg = 1) {
  return new RoundedBoxGeometry(w, h, d, seg, r);
}

function at(g, x, y, z, rx = 0, ry = 0, rz = 0) {
  if (rx) g.rotateX(rx);
  if (ry) g.rotateY(ry);
  if (rz) g.rotateZ(rz);
  g.translate(x, y, z);
  return g;
}

function limb(radius, length, radial = 8) {
  const g = new CapsuleGeometry(radius, length, 3, radial);
  g.translate(0, -length / 2, 0);
  return g;
}

function starShape(outer, inner) {
  const s = new Shape();
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = Math.PI / 2 + (i * Math.PI) / 5;
    const x = Math.cos(a) * r;
    const y = Math.sin(a) * r;
    if (i === 0) s.moveTo(x, y); else s.lineTo(x, y);
  }
  s.closePath();
  return s;
}

/**
 * A flat rounded rectangle extruded upward (top face at y = height), with soft bevelled edges.
 * @param {number} w
 * @param {number} d
 * @param {number} height
 * @param {number} radius plan radius
 * @param {number} bevel
 */
export function roundedSlab(w, d, height, radius, bevel = 0.1) {
  const r = Math.min(radius, w / 2 - 0.01, d / 2 - 0.01);
  const x = -w / 2 + bevel;
  const y = -d / 2 + bevel;
  const W = w - 2 * bevel;
  const D = d - 2 * bevel;
  const rr = Math.max(0.01, r - bevel);
  const s = new Shape();
  s.moveTo(x + rr, y);
  s.lineTo(x + W - rr, y);
  s.quadraticCurveTo(x + W, y, x + W, y + rr);
  s.lineTo(x + W, y + D - rr);
  s.quadraticCurveTo(x + W, y + D, x + W - rr, y + D);
  s.lineTo(x + rr, y + D);
  s.quadraticCurveTo(x, y + D, x, y + D - rr);
  s.lineTo(x, y + rr);
  s.quadraticCurveTo(x, y, x + rr, y);
  const g = new ExtrudeGeometry(s, {
    depth: Math.max(0.01, height - 2 * bevel), bevelEnabled: true, bevelThickness: bevel,
    bevelSize: bevel, bevelSegments: 4, curveSegments: 10,
  });
  // Shape is in XY, extruded along +Z: lay it flat with the top face up.
  g.rotateX(-Math.PI / 2);
  g.translate(0, bevel, 0);
  g.computeVertexNormals();
  return g;
}

/**
 * A round platform with a soft rounded rim (the hall).
 * @param {number} r
 * @param {number} h
 */
export function roundPlatform(r, h, rim = 0.16) {
  // Profile from the bottom centre to the top centre (LatheGeometry wants increasing y for outward normals).
  const pts = [new Vector2(0, 0)];
  const steps = 6;
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * (Math.PI / 2);
    pts.push(new Vector2(r - rim + Math.sin(a) * rim, rim - Math.cos(a) * rim));
  }
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * (Math.PI / 2);
    pts.push(new Vector2(r - rim + Math.cos(a) * rim, h - rim + Math.sin(a) * rim));
  }
  pts.push(new Vector2(0, h));
  return new LatheGeometry(pts, 72);
}

/**
 * A flat nine-slice quad (lying on y = 0) for aoRectTexture: the texture's rectangle edge (uv 0.25)
 * lands exactly on the w × d footprint, the shadow spreads `spread` units beyond it, and the
 * corners keep their size whatever the footprint.
 * @param {number} w
 * @param {number} d
 * @param {number} spread
 */
export function nineSliceAo(w, d, spread = 1.1) {
  const g = new PlaneGeometry(1, 1, 3, 3);
  const unitsPerUv = spread / 0.25;
  const border = 0.44 * unitsPerUv;
  const xs = [-w / 2 - spread, -w / 2 - spread + border, w / 2 + spread - border, w / 2 + spread];
  const zs = [-d / 2 - spread, -d / 2 - spread + border, d / 2 + spread - border, d / 2 + spread];
  // Keep the middle band ordered even for tiny footprints.
  if (xs[1] > xs[2]) xs[1] = xs[2] = 0;
  if (zs[1] > zs[2]) zs[1] = zs[2] = 0;
  const uvs = [0, 0.44, 0.56, 1];
  const pos = g.attributes.position;
  const uv = g.attributes.uv;
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 4; col++) {
      const i = row * 4 + col;
      pos.setXYZ(i, xs[col], 0, zs[row]);
      uv.setXY(i, uvs[col], 1 - uvs[row]);
    }
  }
  // Row 0 (the plane's top edge) goes to -z: the same as rotateX(-π/2), so the quad faces +Y.
  g.computeVertexNormals();
  return g;
}

/** Builds every shared geometry. Returns a map name → BufferGeometry (dispose with disposeAll). */
export function createGeometries() {
  const D = DIMS;
  const H = D.deskH;
  const g = {};

  // Furniture (desk space). Desk top and legs are unit-width and scaled per desk.
  g.deskTop = rbox(1, 0.06, 1, 0.025, 2);
  g.deskTop.translate(0, H - 0.03, 0);
  g.deskLegs = merge([
    at(rbox(0.05, H - 0.06, 0.86, 0.015), -0.45, (H - 0.06) / 2, 0),
    at(rbox(0.05, H - 0.06, 0.86, 0.015), 0.45, (H - 0.06) / 2, 0),
    at(rbox(0.9, 0.22, 0.03, 0.01), 0, H - 0.2, 0.3),
  ]);
  g.monitor = merge([
    at(rbox(0.64, 0.4, 0.045, 0.02), 0, H + 0.34, 0.2),
    at(rbox(0.05, 0.16, 0.03, 0.01), 0, H + 0.1, 0.235),
    at(rbox(0.24, 0.02, 0.16, 0.008), 0, H + 0.01, 0.23),
  ]);
  g.screen = new PlaneGeometry(0.58, 0.34);
  g.screen.rotateY(Math.PI);
  g.screen.translate(0, H + 0.34, 0.2 - 0.024);
  g.screenGlow = new PlaneGeometry(1.1, 0.7);
  g.screenGlow.rotateX(-Math.PI / 2);
  g.screenGlow.translate(0, H + 0.004, 0.02);
  g.keyboard = merge([
    at(rbox(0.44, 0.022, 0.14, 0.008), 0, H + 0.011, -0.24),
    at(rbox(0.06, 0.026, 0.095, 0.012), 0.33, H + 0.013, -0.22),
  ]);

  // Chair (seat space: origin under the seat, facing +Z).
  g.chair = merge([
    at(rbox(0.48, 0.08, 0.46, 0.035, 2), 0, 0.38, 0),
    at(rbox(0.46, 0.5, 0.07, 0.035, 2), 0, 0.72, -0.25, -0.12),
    at(new CylinderGeometry(0.03, 0.03, 0.3, 6, 1, true), 0, 0.2, 0),
    at(new CylinderGeometry(0.24, 0.26, 0.045, 5), 0, 0.03, 0),
  ]);

  // Desk props (placed around their own origin).
  g.mug = merge([
    new CylinderGeometry(0.045, 0.04, 0.1, 14).translate(0, 0.05, 0),
    at(new TorusGeometry(0.03, 0.009, 6, 12), 0.05, 0.05, 0),
  ]);
  g.papers = merge([
    at(rbox(0.22, 0.02, 0.3, 0.005), 0, 0.01, 0, 0, 0.1),
    at(rbox(0.22, 0.02, 0.3, 0.005), 0.02, 0.03, 0.01, 0, -0.08),
  ]);
  g.smallPot = new CylinderGeometry(0.06, 0.045, 0.1, 10).translate(0, 0.05, 0);
  g.smallLeaves = merge([
    at(new IcosahedronGeometry(0.08, 0), 0, 0.16, 0),
    at(new IcosahedronGeometry(0.06, 0), 0.04, 0.22, 0.02),
  ]);

  // Pod props.
  g.pot = merge([
    new CylinderGeometry(0.24, 0.18, 0.42, 16).translate(0, 0.21, 0),
    new CylinderGeometry(0.255, 0.255, 0.05, 16).translate(0, 0.415, 0),
  ]);
  g.foliage = merge([
    at(new IcosahedronGeometry(0.34, 1), 0, 0.78, 0),
    at(new IcosahedronGeometry(0.25, 1), 0.16, 1.05, 0.06),
    at(new IcosahedronGeometry(0.22, 1), -0.14, 1.0, -0.08),
    at(new IcosahedronGeometry(0.18, 1), 0.02, 1.25, -0.02),
  ]);
  g.lampPole = merge([
    new CylinderGeometry(0.18, 0.2, 0.04, 20).translate(0, 0.02, 0),
    new CylinderGeometry(0.018, 0.018, 1.55, 8).translate(0, 0.8, 0),
  ]);
  g.lampShade = new CylinderGeometry(0.16, 0.26, 0.28, 20, 1, false).translate(0, 1.62, 0);
  g.shelf = merge([
    at(new BoxGeometry(0.05, 1.3, 0.36), -0.8, 0.65, 0),
    at(new BoxGeometry(0.05, 1.3, 0.36), 0.8, 0.65, 0),
    at(new BoxGeometry(1.64, 0.04, 0.36), 0, 0.03, 0),
    at(new BoxGeometry(1.64, 0.04, 0.36), 0, 0.45, 0),
    at(new BoxGeometry(1.64, 0.04, 0.36), 0, 0.88, 0),
    at(new BoxGeometry(1.64, 0.04, 0.36), 0, 1.3, 0),
    at(new BoxGeometry(1.6, 1.28, 0.02), 0, 0.65, -0.17),
  ]);
  g.book = new BoxGeometry(1, 1, 1).translate(0, 0.5, 0);
  g.stud = new SphereGeometry(0.045, 8, 6);

  // Lead badge: a small five-point star.
  const star = new ExtrudeGeometry(starShape(0.13, 0.058), {
    depth: 0.035, bevelEnabled: true, bevelThickness: 0.015, bevelSize: 0.015, bevelSegments: 2,
  });
  star.translate(0, 0, -0.0175);
  g.star = star;

  // Unit shapes for floors (scaled by instance matrices).
  g.plane = new PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
  g.ring = new RingGeometry(0.8, 1, 48).rotateX(-Math.PI / 2);
  g.beam = new CylinderGeometry(0.1, 0.1, 1, 16, 1, true).translate(0, 0.5, 0);
  g.pillar = new CylinderGeometry(0.22, 0.22, 1, 16).translate(0, 0.5, 0);

  // Body parts (pivot conventions in BODY).
  const B = BODY;
  g.torso = new CapsuleGeometry(0.19, 0.22, 5, 14);
  g.head = new SphereGeometry(B.headR, 20, 14);
  g.eye = new SphereGeometry(0.027, 8, 6);
  g.hairCap = new SphereGeometry(B.headR + 0.014, 20, 9, 0, Math.PI * 2, 0, Math.PI * 0.53);
  g.hairLong = rbox(0.4, 0.4, 0.14, 0.06, 3);
  g.bun = new SphereGeometry(0.1, 14, 10);
  const curls = [];
  const rc = rng32(7);
  for (let i = 0; i < 16; i++) {
    const phi = rc() * Math.PI * 0.55;
    const th = rc() * Math.PI * 2;
    const r = B.headR + 0.02;
    const s = new SphereGeometry(0.075 + rc() * 0.02, 8, 6);
    s.translate(Math.sin(phi) * Math.cos(th) * r, Math.cos(phi) * r, Math.sin(phi) * Math.sin(th) * r - 0.02);
    curls.push(s);
  }
  g.curly = merge(curls);
  g.cap = merge([
    new SphereGeometry(B.headR + 0.018, 20, 8, 0, Math.PI * 2, 0, Math.PI * 0.47),
    at(new CylinderGeometry(0.15, 0.15, 0.022, 20).scale(1, 1, 1.3), 0, 0.07, 0.17, 0.12),
  ]);
  g.headphones = merge([
    at(new TorusGeometry(B.headR + 0.03, 0.018, 6, 20, Math.PI), 0, 0.0, 0),
    at(new CylinderGeometry(0.07, 0.07, 0.06, 14), B.headR + 0.02, 0, 0, 0, 0, Math.PI / 2),
    at(new CylinderGeometry(0.07, 0.07, 0.06, 14), -B.headR - 0.02, 0, 0, 0, 0, Math.PI / 2),
  ]);
  g.upperArm = limb(0.06, B.upperArm);
  g.foreArm = limb(0.054, B.foreArm);
  g.hand = new SphereGeometry(0.06, 10, 8);
  g.thigh = limb(0.083, B.thigh);
  g.shin = limb(0.07, B.shin);
  g.shoe = rbox(0.13, 0.08, 0.21, 0.035).translate(0, -0.03, 0.04);

  chickenGeometries(g);
  catGeometries(g);
  return g;
}

function rng32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Disposes every geometry of a map built by createGeometries. */
export function disposeAll(map) {
  for (const geo of Object.values(map)) geo?.dispose?.();
}
